import { defineStore } from 'pinia';
import { useAuthStore, useCommunityStore } from '.';
import apiService from '@/services/api.service';
import { Config } from '@/config';

import type {
  ConsultationDto,
  DeleteMyAnswersResult,
  Pagination,
  ConsultationListItemDto,
  SectionDto,
  UserPermissionDto,
  Locales,
  LocalizableString,
} from '@/services/types/api.types';
import { Constants } from '@/constants';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';
import { buildTransportPayload } from '@/helpers/store.helper';
import { SavingError } from '@/errors';
import { i18n } from '@/plugins/i18n';
import { isInvalidVisibilityRules } from '@/helpers/visibilityRules.helper';

const findConsultation = (consultations: ConsultationDto[], consultationId: number) => {
  return consultations.find((x) => {
    return x.id == consultationId;
  });
};

const buildConsultationPayload = (consultation: ConsultationDto): Partial<ConsultationDto> => {
  return buildTransportPayload(consultation, {
    normalize: [
      (payload) => {
        if ((payload.private_email_allowed?.email?.length ?? 0) > Constants.Consultation.PrivateEmailLimit) {
          delete payload.private_email_allowed;
        }
      },
    ],
  });
};

export interface ConsultationsState {
  consultations: ConsultationDto[];
  consultationListItems: ConsultationListItemDto[];
  pagination: Pagination;
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

function getStatuses(): any[] {
  const { t } = useI18n();
  return [
    { label: t('consultation.status.options.pre-launch'), value: Constants.Consultation.Status.PreLaunch },
    { label: t('consultation.status.options.ongoing'), value: Constants.Consultation.Status.Ongoing },
    { label: t('consultation.status.options.completed'), value: Constants.Consultation.Status.Completed },
    { label: t('consultation.status.options.archived'), value: Constants.Consultation.Status.Archived },
  ];
}

export const useConsultationsStore = defineStore({
  id: 'consultations',
  state: (): ConsultationsState => ({
    consultations: [],
    consultationListItems: [],
    pagination: {
      page: 1,
      rowsNumber: 0,
      rowsPerPage: Constants.Pagination.DefaultRowsPerPage,
    },
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    getConsultation: (state) => (consultationId: number) => {
      return findConsultation(state.consultations, consultationId);
    },
    orderedConsultations: (state) => {
      return [...state.consultations].sort((a, b) => a.order_num - b.order_num);
    },
    getSections: (state) => (consultationId: number) => {
      return findConsultation(state.consultations, consultationId)?.sections;
    },
    getSortedSections: (state) => (consultationId: number) => {
      return findConsultation(state.consultations, consultationId)?.sections?.sort((a, b) => a.order_num - b.order_num);
    },
    getSortedContexts: (state) => (consultationId: number) => {
      return findConsultation(state.consultations, consultationId)?.contexts?.sort((a, b) => a.order_num - b.order_num);
    },
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): ConsultationDto | undefined =>
        state.consultations.find((c) => c.id === id),
    getConsultationStatuses: (state) => getStatuses,
    previewURL: (state) => (consultationId: number) => {
      const consultation = findConsultation(state.consultations, consultationId);
      const communityStore = useCommunityStore();
      if (consultation) {
        const communityUrl = communityStore.previewURL(consultation.community_id);
        return `${communityUrl}/consultations/${consultation.slug}`;
      }
    },
    isCompleted: (state) => (consultationId: number) => {
      const consultation = findConsultation(state.consultations, consultationId);
      return [Constants.Consultation.Status.Completed, Constants.Consultation.Status.Archived].includes(
        consultation?.status ?? -1,
      );
    },
  },
  actions: {
    async fetchConsultation(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<ConsultationDto>(`${Config.api.v2}/consultations/${id}`);
        const index = this.consultations.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.consultations[index] = data;
        } else {
          this.consultations.push(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },
    async fetchConsultations(
      page: number = 1,
      rowsPerPage: number = Constants.Pagination.DefaultRowsPerPage,
      q: string | null = null,
    ) {
      this.isLoading = true;
      try {
        const data = { page, pageSize: rowsPerPage, q };
        const response = await apiService.post<ConsultationListItemDto[]>(
          `${Config.api.v2}/consultations/:search`,
          data,
        );
        if (response.success) {
          this.consultationListItems = response.data ?? [];
          if (!response.pagination) return;
          this.pagination = {
            page: response.pagination.currentPage,
            rowsNumber: response.pagination.totalCount,
            rowsPerPage: rowsPerPage,
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchSections(consultationId: number) {
      const authStore = useAuthStore();

      this.isLoading = true;
      this.error = null;

      try {
        //TOOD: encapsulate axios, set common headers, parameterize base url, proper error handling
        const response = await apiService.get(`${Config.api.v2}/consultations/${consultationId}/sections`);
        const dto = response.data;
        const consultation = this.consultations.find((x) => {
          return x.id == consultationId;
        });
        if (consultation) {
          consultation.sections = dto;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchUserPermissions(consultationId: number) {
      try {
        const { data } = await apiService.get<UserPermissionDto[]>(
          `${Config.api.v2}/consultations/${consultationId}/user-permissions`,
        );
        const consultation = findConsultation(this.consultations, consultationId);
        if (!consultation) {
          throw new Error('Consultation not found');
        }
        consultation.user_permissions = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async addUserPermission(consultationId: number, email: string, role: number): Promise<boolean> {
      try {
        console.debug('Creating user permission', email);
        const response = await apiService.post(`${Config.api.v2}/consultations/${consultationId}/user-permissions`, {
          email,
          role,
        });
        await this.fetchUserPermissions(consultationId);
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteUserPermission(up: UserPermissionDto) {
      try {
        await apiService.delete(`${Config.api.v2}/consultations/${up.object_id}/user-permissions/${up.id}`);
        await this.fetchUserPermissions(up.object_id);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async updateConsultations(...consultations: ConsultationDto[]) {
      try {
        for (const consultation of consultations) {
          const payload = buildConsultationPayload(consultation);
          const response = await apiService.put(`${Config.api.v2}/consultations/${consultation.id}`, payload);
          // Handle the response, e.g., updating the state, calling mutations, etc.
        }
      } catch (error) {
        console.error(error);
      }
    },
    async updateSections(...sections: SectionDto[]) {
      try {
        for (const section of sections) {
          const response = await apiService.put(`${Config.api.v2}/sections/${section.id}`, section);
          // Handle the response, e.g., updating the state, calling mutations, etc.
        }
      } catch (error) {
        console.error(error);
      }
    },
    createConsultation(options: {
      title?: LocalizableString;
      description?: LocalizableString;
      cloneFromId?: number;
      language_enabled: Locales[];
    }): ConsultationDto {
      const authStore = useAuthStore();
      return {
        community_id: authStore.currentCommunity ?? -1,
        is_template: 0,
        clone_from_id: options.cloneFromId,
        slug: '',
        status: Constants.Consultation.Status.PreLaunch,
        title: options.title ?? { en: '', fr: '' },
        description: options.description ?? { en: '', fr: '' },
        type: Constants.Consultation.Type.Public,
        participation_mode: Constants.Consultation.ParticipationMode.Open,
        options: {
          citizen_proposal: true,
          map: false,
          anonymous_comments: false,
          show_private_everyone: false,
          mailingList: false,
          automatic_publish: false,
          guided_consultation: {
            resultsMode: Constants.Consultation.GuidedConsultation.ResultsMode.CompletedOnly,
            visibility: { ruleSets: [] },
          },
        },
        order_num: -1,
        attachmentsOpened: false,
        private_email_allowed: { email: [], domain: [] },
        banner_file: null,
        open_graph_file: null,
        projects: [],
        user_permissions: [],
        language_enabled: options.language_enabled,
        conclusion_message: {
          enabled: false,
          content: { en: '', fr: '' },
        },
      };
    },
    async save(consultation: ConsultationDto) {
      try {
        const payload = buildConsultationPayload(consultation);
        let consultationId = consultation.id;
        if (consultationId) {
          await apiService.put<ConsultationDto>(`${Config.api.v2}/consultations/${consultation.id}`, payload);
        } else {
          const { data } = await apiService.post<ConsultationDto>(`${Config.api.v2}/consultations`, payload);
          consultationId = data.id;
          // todo: deplacer dans le Popup de creation
          router.push({ name: 'consultation_details', params: { id: consultationId } });
        }
        this.fetchConsultation(consultationId!);
        return true;
      } catch (error) {
        console.error(error);
        if (isInvalidVisibilityRules(error)) {
          throw new SavingError(i18n.global.t('form.guided-consultation.saveRejected'));
        }
        return false;
      }
    },
    async saveBanner(consultationId: number, options: SavingOptions) {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/consultations/${consultationId}/banner`,
        });
        await this.fetchConsultation(consultationId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async removeBanner(consultationId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/consultations/${consultationId}/banner`);
        await this.fetchConsultation(consultationId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async saveOpenGraph(consultationId: number, options: SavingOptions) {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/consultations/${consultationId}/open-graph`,
        });
        await this.fetchConsultation(consultationId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async removeOpenGraph(consultationId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/consultations/${consultationId}/open-graph`);
        await this.fetchConsultation(consultationId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async delete(consultationId: number, page: number, q: string | null = null) {
      try {
        await apiService.delete(`${Config.api.v2}/consultations/${consultationId}`);
        this.fetchConsultations(page, this.pagination.rowsPerPage, q);
      } catch (error) {
        console.error(error);
      }
    },
    async removeVote(consultationId: number) {
      try {
        await apiService.post(`${Config.api.v2}/consultations/${consultationId}/:reset-vote`);
        this.fetchConsultations();
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async resetMyVotes(consultationId: number): Promise<DeleteMyAnswersResult | null> {
      try {
        const response = await apiService.post<DeleteMyAnswersResult>(
          `${Config.api.v2}/consultations/${consultationId}/:reset-my-votes`,
        );
        await this.fetchConsultation(consultationId);
        return response.data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async getExcelReport(consultationId: number, language: string) {
      try {
        return await apiService.getBlobRequest(
          `${Config.api.v2}/reports/excel?consultationId=${consultationId}&language=${language}`,
        );
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async getPDFReport(
      consultationId: number,
      language: string,
      filterParameters:
        | { formId: number; valueId: number | string }
        | Array<{ formId: number; valueId: number | string }>
        | null = null,
    ) {
      try {
        const filters = Array.isArray(filterParameters) ? filterParameters : filterParameters ? [filterParameters] : [];

        return await apiService.post(`${Config.api.v2}/reports/pdf`, {
          consultationId,
          language,
          filters,
        });
      } catch (error) {
        console.error(error);
        return { success: false };
      }
    },
    async getRawCsvReport(consultationId: number, language: string) {
      try {
        const response = await apiService.getBlobRequest(
          `${Config.api.v2}/reports/raw?consultationId=${consultationId}&language=${language}&format=zip`,
        );
        return response;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async fetchConsultationStats(consultationId: number, timePeriod: number) {
      const requestData = { timePeriod };
      try {
        return await apiService.get(`${Config.api.v2}/consultations/${consultationId}/visitor-metrics`, requestData);
      } catch (error) {
        console.error(error);
        return { data: [], success: false, error: error instanceof Error ? error.message : 'Unknown error' };
      }
    },
  },
});
